import React, { useEffect, useState, useMemo } from 'react';
import {
    View,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    useWindowDimensions,
    FlatList,
    RefreshControl,
    DeviceEventEmitter,
    ActivityIndicator,
} from 'react-native';
import moment from 'moment';

import { appColors, fontSize } from '../../../themes/appTheme';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import CommonService from '../../../shared/services/commonService';
import { useAppNavigation, useAppRoute } from '../../../types/@react-navigation/navigation';
import { getAllStaffReport, staffReportData } from '../../../redux/slices/staffReportSlice';

import { useIsFocused } from '@react-navigation/native';

import {
    ZenDateTimePicker,
    ZenIcon,
    ZenLoader,
    ZenText,
    ZenEmptyComponent,
    ZenToast,
    ZenBadge,
} from 'zen-core-elements';
import CommonHeader from '../../../shared/commonComponents/commonHeader';
import SellscreenModal from '../../newSellscreen/sellscreenModal';
import { ThemeConsumer } from '@rneui/themed';
import useBackHandler from '../../../shared/hooks/useBackHandler';

type WorkItem = {
    workDate: string;
    totalWorkingTime: string;
};

type OrderItem = {
    orderDate: string;
    ordersCount: number;
    discounts: number;
    totalSales: number;
    netSales: number;
};

type StaffRow = staffReportData;

const limit = 10;

const formatWorkingTime = (val: string | number | undefined | null) => {
    if (!val) return '00:00 hrs';
    const str = String(val).trim();
    if (str.includes('hrs')) return str;
    if (str.includes(':')) {
        const parts = str.split(':');
        const h = (parts[0] || '0').padStart(2, '0');
        const m = (parts[1]?.slice(0, 2) || '0').padStart(2, '0');
        return `${h}:${m} hrs`;
    }
    const num = parseFloat(str);
    if (isNaN(num)) return `${str} hrs`;
    const hours = Math.floor(num);
    const minutes = Math.round((num - hours) * 60);
    const hh = String(hours).padStart(2, '0');
    const mm = String(minutes).padStart(2, '0');
    return `${hh}:${mm} hrs`;
};

const StaffReportPage = () => {
    const isFocused = useIsFocused();
    const { isLandscape } = useAppSelector((state) => state.common);
    const { width, height } = useWindowDimensions();
    const dispatch = useAppDispatch();
    const navigation = useAppNavigation();
    const route = useAppRoute("StaffReport");
    const { id: locationId } = useAppSelector(state => state?.common?.currentLocation)

    useBackHandler()
    const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
    const { value: staffReportList, loading: loadPage, count: totalCount, from: fromDate, to: toDate } = useAppSelector((state) => state?.staffReport?.staffDataValue);

    const [datePickerVisibile, setDatePickerVisible] = useState<{ visibilty: boolean; current: 'from' | 'to' | null }>({
        visibilty: false,
        current: null
    });
    const [dateRange, setDateRange] = useState<{ from: string | null; to: string | null }>({
        from: route?.params?.beginDate || null,
        to: route?.params?.endDate || null
    });
    const [offset, setOffset] = useState<number>(0);
    const [footerLoader, setFooterLoader] = useState<boolean>(false);
    const [selectedStaff, setSelectedStaff] = useState<StaffRow | null>(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [refresh, setRefresh] = useState<boolean>(false);

    const reInitializeStates = () => {
        const resetRange = { from: route?.params?.beginDate || null, to: route?.params?.endDate || null };
        setDateRange(resetRange);
        setSelectedStaff(null);
    };

    useEffect(() => {
        const staffReportUpdate = DeviceEventEmitter.addListener("STAFF_REPORT_UPDATED", () => {
            const resetRange = { from: route?.params?.beginDate || null, to: route?.params?.endDate || null };
            setDateRange(resetRange);
            setSelectedStaff(null);
            setOffset(0);
            fetchStaffReports(0, true, resetRange);
        });
        return () => {
            staffReportUpdate?.remove();
        };
    }, [route?.params?.beginDate, route?.params?.endDate]);

    useEffect(() => {
        if (isFocused && route?.params?.beginDate && route?.params?.endDate) {
            setDateRange({
                from: route.params.beginDate,
                to: route.params.endDate,
            });
        }
    }, [isFocused, route?.params?.beginDate, route?.params?.endDate]);

    useEffect(() => {
        if (isFocused && dateRange?.from && dateRange?.to) {
            setOffset(0);
            fetchStaffReports(0);
        }
    }, [isFocused, dateRange]);

    const fetchStaffReports = async (currentOffset = 0, forceRefresh = false, range = dateRange) => {
        const payload = {
            offset: currentOffset,
            limit: limit,
            filterData: JSON.stringify({ customDateRange: { begin: range?.from, end: range?.to } }),
            locationId: locationId
        };
        dispatch(getAllStaffReport({ data: payload })).unwrap().then(() => {
        }).catch((err) => {
            ZenToast({ type: 'error', message: err?.message ?? 'Something went wrong' });
        }).finally(() => {
            setRefresh(false);
            setFooterLoader(false);
        });
    };

    const staffRows: StaffRow[] = (staffReportList as StaffRow[]) || [];

    useEffect(() => {
        if (isLandscape && staffRows.length > 0) {
            if (!selectedStaff || !staffRows.some((s) => s.staffId === selectedStaff.staffId)) {
                //In initial fetch, in landscape view ,selected staff will be the first one
                setSelectedStaff(staffRows[0]);
            }
        } else if (!isLandscape) {
            //In initial fetch ,in potrait view modal will not be shown
            setModalVisible(!!selectedStaff);
        }
    }, [isLandscape, staffRows]);

    const handleSelectStaff = (staff: StaffRow) => {
        setSelectedStaff(staff);
        if (!isLandscape) setModalVisible(true);
    };

    const handleCloseModal = () => {
        //While closing the modal in potrait view, and by turning to landscape. The first staff will be selected in default
        setModalVisible(false);
        if (!isLandscape) setSelectedStaff(null);
    };

    const onEndReached = () => {
        if (!footerLoader && (totalCount || 0) > offset + limit) {
            setFooterLoader(true);
            const nextOffset = offset + limit;
            setOffset(nextOffset);
            fetchStaffReports(nextOffset);
        }
    };

    const onRefresh = () => {
        setRefresh(true);
        const resetRange = { from: route?.params?.beginDate || null, to: route?.params?.endDate || null };
        setDateRange(resetRange);
        setSelectedStaff(null);
        setOffset(0);
        fetchStaffReports(0, true, resetRange);
    };

    const footerComponent = () => {
        return (
            <ThemeConsumer>
                {({ theme }) => (
                    footerLoader ?
                        <View style={{ paddingVertical: 10 }}>
                            <ActivityIndicator size={"small"} color={theme?.primary} />
                        </View>
                        : undefined
                )}
            </ThemeConsumer>
        );
    };

    // Calculate Summary Stats
    const totalStaffCount = totalCount || staffRows.length || 0;
    const activeStaffCount = staffRows.filter((s) => (s?.work && s.work.length > 0) || (s?.order && s.order.length > 0)).length || staffRows.length || 0;
    const reportsCount = staffRows.reduce((acc, s) => {
        const dates = new Set([...(s.work || []).map((w) => w.workDate), ...(s.order || []).map((o) => o.orderDate)]);
        return acc + dates.size;
    }, 0) || staffRows.length || 0;

    const getStaffDatesData = (staff: StaffRow | null) => {
        if (!staff) return [];
        const allDates = Array.from(
            new Set([
                ...(staff.work ?? []).map((w) => w.workDate),
                ...(staff.order ?? []).map((o) => o.orderDate),
            ])
        ).sort((a, b) => {
            if (moment(a).isValid() && moment(b).isValid()) {
                return moment(b).diff(moment(a));
            }
            return b.localeCompare(a);
        });

        return allDates.map((date) => {
            const workItem = staff.work?.find((w) => w.workDate === date);
            const matchingOrder = staff.order?.find((o) => o.orderDate === date);
            return {
                date,
                workItem,
                matchingOrder,
                ordersCount: matchingOrder?.ordersCount ?? 0,
                discounts: matchingOrder?.discounts ?? 0,
                netSales: matchingOrder?.netSales ?? 0,
                totalSales: matchingOrder?.totalSales ?? 0,
                totalWorkingTime: workItem?.totalWorkingTime ?? '0',
            };
        });
    };

    // Component: Date Filter Box
    const DateFilterSection = () => (
        <View style={styles.dateFilterContainer}>
            <View style={styles.dateInputsRow}>
                {/* From Date */}
                <TouchableOpacity
                    style={styles.dateBox}
                    onPress={() => setDatePickerVisible({ visibilty: true, current: 'from' })}
                >
                    <ZenIcon type="material-community" name="calendar-range" size={18} color={appColors.blue600} />
                    <View style={{ marginLeft: 8 }}>
                        <ZenText content="From" size={fontSize.errorFontSize} color={appColors.lightDark} />
                        <ZenText content={dateRange.from || 'Select'} size={fontSize.tinyFontSize} bold color={appColors.dark} />
                    </View>
                </TouchableOpacity>

                <View style={styles.dateDivider} />

                {/* To Date */}
                <TouchableOpacity
                    style={styles.dateBox}
                    onPress={() => setDatePickerVisible({ visibilty: true, current: 'to' })}
                >
                    <ZenIcon type="material-community" name="calendar-check" size={18} color={appColors.blue600} />
                    <View style={{ marginLeft: 8 }}>
                        <ZenText content="To" size={fontSize.errorFontSize} color={appColors.lightDark} />
                        <ZenText content={dateRange.to || 'Select'} size={fontSize.tinyFontSize} bold color={appColors.dark} />
                    </View>
                </TouchableOpacity>
            </View>
        </View>
    );

    // Component: Stat Card
    const StatCard = ({ title, value, iconName, color }: { title: string; value: string | number; iconName: string; color: string }) => (
        <View style={[styles.statCard, !isLandscape && styles.statCardPortrait]}>
            <View style={[styles.iconContainer, { backgroundColor: color + '15' }]}>
                <ZenIcon type="material-community" name={iconName} size={22} color={color} />
            </View>
            <View style={!isLandscape ? styles.statTextContainerPortrait : styles.statTextContainer}>
                <ZenText content={title} color={appColors.lightDark} size={fontSize.smallHeaderFontSize} />
                <ZenText content={String(value)} bold color={appColors.dark} size={isLandscape ? fontSize.largerFontSize : fontSize.mediumFontSize} style={{ marginTop: 2 }} />
            </View>
        </View>
    );

    // Component: Staff List Card
    const renderStaffCard = (staff: StaffRow) => {
        const isSelected = isLandscape && selectedStaff?.staffId === staff?.staffId;
        const initial = (staff?.staffName?.trim()?.charAt(0) || 'S').toUpperCase();
        const joinedFormatted = staff?.createdDate && moment(staff.createdDate).isValid()
            ? moment(staff.createdDate).format('DD MMM YYYY')
            : (staff?.createdDate || 'N/A');

        return (
            <TouchableOpacity
                key={staff.staffId}
                activeOpacity={0.7}
                onPress={() => handleSelectStaff(staff)}
                style={[
                    styles.staffCard,
                    isSelected && styles.staffCardSelected,
                ]}
            >
                <View style={styles.avatar}>
                    <ZenText content={initial} bold color={appColors.primary} size={fontSize.mediumFontSize} />
                </View>
                <View style={styles.staffInfo}>
                    <ZenText content={staff?.staffName || 'N/A'} bold size={fontSize.smallHeaderFontSize} color={appColors.dark} numberOfLines={1} />
                    <ZenText content={staff?.staffRole || 'N/A'} color={appColors.lightDark} size={fontSize.smallFontSize} style={{ marginTop: 2 }} numberOfLines={1} />
                </View>
                <View style={styles.joinedContainer}>
                    <ZenText content="Joined" color={appColors.lightDark} size={fontSize.errorFontSize} style={{ textAlign: 'right' }} />
                    <ZenText content={joinedFormatted} bold color={appColors.dark} size={fontSize.tinyFontSize} style={{ marginTop: 2, textAlign: 'right' }} />
                </View>
            </TouchableOpacity>
        );
    };

    return (
        <View style={styles.container}>
            <CommonHeader
                headerName="Staff Report"
                backIcon={true}
                backIconOnpress={() => navigation.goBack()}
            />

            {loadPage && offset === 0 && !refresh ? (
                <ZenLoader lottie={require('../../../assets/infinityLoader.json')} lottieType="Local" />
            ) : (
                <View style={styles.landscapeLayout}>
                    {/* LEFT COLUMN: Filter & Staff List */}
                    <View style={{ flex: (isLandscape && staffRows?.length > 0) ? 0.32 : 1 }}>
                        <DateFilterSection />

                        <FlatList
                            data={staffRows}
                            renderItem={({ item }) => renderStaffCard(item)}
                            keyExtractor={(item) => item.staffId.toString()}
                            showsVerticalScrollIndicator={false}
                            refreshControl={<RefreshControl refreshing={refresh} onRefresh={onRefresh} />}
                            onEndReached={onEndReached}
                            onEndReachedThreshold={0.5}
                            ListFooterComponent={footerComponent}
                            ListEmptyComponent={<ZenEmptyComponent viewStyle={{ backgroundColor: appColors?.light }} />}
                            ListHeaderComponent={
                                (!isLandscape ?
                                    <View>
                                        <View style={styles.mobileKpiContainer}>
                                            <StatCard title="Total Staff" value={totalStaffCount} iconName="account-group" color="#4F46E5" />
                                            <StatCard title="Active Staff" value={activeStaffCount} iconName="account-check" color="#10B981" />
                                            <StatCard title="Reports Count" value={reportsCount} iconName="file-document-outline" color="#F59E0B" />
                                        </View>
                                        {staffRows.length > 0 && (
                                            <ZenText
                                                content="STAFF LIST"
                                                color={appColors.lightDark}
                                                size={fontSize.errorFontSize}
                                                style={styles.mobileSectionTitle}
                                                bold
                                            />
                                        )}
                                    </View>
                                    : <></>)
                            }
                            contentContainerStyle={{ flexGrow: staffRows?.length ? undefined : 1, paddingBottom: 20 }}
                        />
                    </View>

                    {/* RIGHT COLUMN: Summary KPI Cards + Data Table (Landscape) */}
                    {(isLandscape && staffRows?.length > 0) && (
                        <View style={styles.landscapeMain}>
                            {/* Top KPI Cards Row */}
                            <View style={styles.kpiRow}>
                                <StatCard title="Total Staff" value={totalStaffCount} iconName="account-group" color="#4F46E5" />
                                <StatCard title="Active Staff" value={activeStaffCount} iconName="account-check" color="#10B981" />
                                <StatCard title="Reports Count" value={reportsCount} iconName="file-document-outline" color="#F59E0B" />
                            </View>

                            {/* Selected Staff Performance Table */}
                            <View style={styles.tableCard}>
                                <View style={styles.tableHeaderRow}>
                                    <ZenText content="DATE" bold color={appColors.lightDark} size={fontSize.errorFontSize} style={styles.colDate} />
                                    <ZenText content="ORDERS COUNT" bold color={appColors.lightDark} size={fontSize.errorFontSize} style={styles.colOrders} />
                                    <ZenText content="DISCOUNT" bold color={appColors.lightDark} size={fontSize.errorFontSize} style={styles.colDiscount} />
                                    <ZenText content="NET SALE" bold color={appColors.lightDark} size={fontSize.errorFontSize} style={styles.colNetSale} />
                                    <ZenText content="TOTAL SALE" bold color={appColors.lightDark} size={fontSize.errorFontSize} style={styles.colTotalSale} />
                                    <ZenText content="TOTAL WORKING TIME" bold color={appColors.lightDark} size={fontSize.errorFontSize} style={styles.colWorkingTime} />
                                </View>

                                <FlatList
                                    data={getStaffDatesData(selectedStaff)}
                                    keyExtractor={(item, index) => item.date || index.toString()}
                                    showsVerticalScrollIndicator={false}
                                    renderItem={({ item }) => (
                                        <View style={styles.tableRow}>
                                            <ZenText
                                                content={moment(item.date).isValid() ? moment(item.date).format('DD-MM-YYYY') : item.date}
                                                bold
                                                color={appColors.dark}
                                                size={fontSize.smallFontSize}
                                                style={styles.colDate}
                                            />
                                            <ZenText
                                                content={String(item.ordersCount)}
                                                color={appColors.dark}
                                                size={fontSize.smallFontSize}
                                                style={styles.colOrders}
                                            />
                                            <ZenText
                                                content={CommonService.currencyStandard(currencySymbol, String(item.discounts ?? 0))}
                                                color={appColors.dark}
                                                size={fontSize.smallFontSize}
                                                style={styles.colDiscount}
                                            />
                                            <ZenText
                                                content={CommonService.currencyStandard(currencySymbol, String(item.netSales ?? 0))}
                                                color={appColors.dark}
                                                size={fontSize.smallFontSize}
                                                style={styles.colNetSale}
                                            />
                                            <ZenText
                                                content={CommonService.currencyStandard(currencySymbol, String(item.totalSales ?? 0))}
                                                color={appColors.dark}
                                                size={fontSize.smallFontSize}
                                                style={styles.colTotalSale}
                                            />
                                            <ZenText
                                                content={formatWorkingTime(item.totalWorkingTime)}
                                                color={appColors.dark}
                                                size={fontSize.smallFontSize}
                                                style={styles.colWorkingTime}
                                            />
                                        </View>
                                    )}
                                    ListEmptyComponent={<ZenEmptyComponent />}
                                    contentContainerStyle={{ flexGrow: 1 }}
                                />
                            </View>
                        </View>
                    )}
                </View>
            )}

            {/* =================== MOBILE MODAL =================== */}
            <SellscreenModal
                isModalOpen={modalVisible && !isLandscape}
                setModalOpen={setModalVisible}
                onCloseRequest={handleCloseModal}
                viewStyle={{ padding: 0 }}
            >
                <View style={styles.modalHeader}>
                    <ZenText
                        content="Staff Report Details"
                        bold
                        size={width > 600 ? fontSize.mediumFontSize : fontSize.smallerFontSize}
                        color={appColors.dark}
                    />
                    <TouchableOpacity onPress={handleCloseModal}>
                        <ZenIcon
                            name="close-circle"
                            type="material-community"
                            size={width > 600 ? 24 : 22}
                            color={appColors.iconColor}
                        />
                    </TouchableOpacity>
                </View>

                {selectedStaff && (
                    <View style={{ height: height * 0.72 }}>
                        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.modalScrollContent}>
                            {/* Staff Profile Header Card */}
                            <View style={styles.modalStaffCard}>
                                <View style={styles.avatarLarge}>
                                    <ZenText
                                        content={(selectedStaff.staffName?.trim()?.charAt(0) || 'S').toUpperCase()}
                                        bold
                                        color={appColors.primary}
                                        size={fontSize.largerFontSize}
                                    />
                                </View>
                                <View style={{ flex: 1, marginLeft: 12 }}>
                                    <ZenText content={selectedStaff.staffName || 'N/A'} bold size={fontSize.largerFontSize} color={appColors.dark} />
                                    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4, gap: 8 }}>
                                        <ZenBadge
                                            badgeName={selectedStaff.staffRole || 'N/A'}
                                            badgeColor="#EEF2FF"
                                            badgeTextColor="#4F46E5"
                                            badgeTextSize={fontSize.badgeFontSize}
                                            badgeContainerStyle={{ paddingVertical: 2, paddingHorizontal: 8, borderRadius: 6 }}
                                        />
                                        <ZenText
                                            content={`Joined: ${selectedStaff.createdDate && moment(selectedStaff.createdDate).isValid() ? moment(selectedStaff.createdDate).format('DD MMM YYYY') : selectedStaff.createdDate || 'N/A'}`}
                                            color={appColors.lightDark}
                                            size={fontSize.tinyFontSize}
                                        />
                                    </View>
                                </View>
                            </View>

                            {/* Detailed Breakdown per Date */}
                            <ZenText
                                content="Performance Breakdown"
                                bold
                                size={fontSize.mediumFontSize}
                                color={appColors.primary}
                                style={{ marginTop: 14, marginBottom: 8 }}
                            />

                            {getStaffDatesData(selectedStaff).length > 0 ? (
                                getStaffDatesData(selectedStaff).map((item, index) => (
                                    <View key={item.date || index} style={styles.modalDateCard}>
                                        {/* Date & Working Time Header */}
                                        <View style={styles.modalDateCardHeader}>
                                            <View style={styles.dateTag}>
                                                <ZenIcon type="material-community" name="calendar" size={16} color={appColors.blue600} />
                                                <ZenText
                                                    content={moment(item.date).isValid() ? moment(item.date).format('DD-MM-YYYY') : item.date}
                                                    bold
                                                    size={fontSize.smallFontSize}
                                                    color={appColors.blue600}
                                                    style={{ marginLeft: 6 }}
                                                />
                                            </View>

                                            <View style={styles.timeTag}>
                                                <ZenIcon type="material-community" name="clock-time-four-outline" size={16} color="#8B5CF6" />
                                                <ZenText
                                                    content={formatWorkingTime(item.totalWorkingTime)}
                                                    bold
                                                    size={fontSize.smallFontSize}
                                                    color="#8B5CF6"
                                                    style={{ marginLeft: 4 }}
                                                />
                                            </View>
                                        </View>

                                        {/* Metrics Grid (Orders Count, Discount, Net Sale, Total Sale) */}
                                        <View style={styles.modalMetricsGrid}>
                                            <View style={styles.modalMetricItem}>
                                                <ZenText content="Orders Count" size={fontSize.errorFontSize} color={appColors.lightDark} />
                                                <ZenText content={String(item.ordersCount)} bold size={fontSize.mediumFontSize} color={appColors.dark} style={{ marginTop: 2 }} />
                                            </View>

                                            <View style={styles.modalMetricItem}>
                                                <ZenText content="Discount" size={fontSize.errorFontSize} color={appColors.lightDark} />
                                                <ZenText
                                                    content={CommonService.currencyStandard(currencySymbol, String(item.discounts ?? 0))}
                                                    bold
                                                    size={fontSize.mediumFontSize}
                                                    color={item.discounts > 0 ? '#F59E0B' : appColors.dark}
                                                    style={{ marginTop: 2 }}
                                                />
                                            </View>

                                            <View style={styles.modalMetricItem}>
                                                <ZenText content="Net Sale" size={fontSize.errorFontSize} color={appColors.lightDark} />
                                                <ZenText
                                                    content={CommonService.currencyStandard(currencySymbol, String(item.netSales ?? 0))}
                                                    bold
                                                    size={fontSize.mediumFontSize}
                                                    color="#10B981"
                                                    style={{ marginTop: 2 }}
                                                />
                                            </View>

                                            <View style={styles.modalMetricItem}>
                                                <ZenText content="Total Sale" size={fontSize.errorFontSize} color={appColors.lightDark} />
                                                <ZenText
                                                    content={CommonService.currencyStandard(currencySymbol, String(item.totalSales ?? 0))}
                                                    bold
                                                    size={fontSize.mediumFontSize}
                                                    color="#4F46E5"
                                                    style={{ marginTop: 2 }}
                                                />
                                            </View>
                                        </View>
                                    </View>
                                ))
                            ) : (
                                <View style={{ paddingVertical: 30, alignItems: 'center' }}>
                                    <ZenEmptyComponent />
                                </View>
                            )}
                        </ScrollView>
                    </View>
                )}
            </SellscreenModal>

            {/* =================== DATE TIME PICKER =================== */}
            {datePickerVisibile?.visibilty && (
                <ZenDateTimePicker
                    date={
                        datePickerVisibile?.current === 'from' ?
                            dateRange?.from ? new Date(dateRange?.from) : new Date()
                            : dateRange?.to ? new Date(dateRange?.to) : new Date()
                    }
                    mode="date"
                    open={datePickerVisibile?.visibilty}
                    maximumDate={datePickerVisibile?.current == 'from' && dateRange?.to ? new Date(dateRange?.to) : new Date()}
                    minimumDate={datePickerVisibile?.current !== 'from' && dateRange?.from ? new Date(dateRange?.from) : undefined}
                    onConfirm={(data) => {
                        setSelectedStaff(null);
                        if (datePickerVisibile?.current == 'from') {
                            const fromDate = moment(data).format('YYYY-MM-DD');
                            const toDate = dateRange?.to || moment(new Date()).format('YYYY-MM-DD');
                            setDateRange({ from: fromDate, to: toDate });
                            setDatePickerVisible({ visibilty: false, current: null });
                        } else {
                            const toDate = moment(data).format('YYYY-MM-DD');
                            const fromDate = dateRange?.from || moment(new Date()).format('YYYY-MM-DD');
                            setDateRange({ from: fromDate, to: toDate });
                            setDatePickerVisible({ visibilty: false, current: null });
                        }
                    }}
                    onCancel={() => setDatePickerVisible({ visibilty: false, current: null })}
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: appColors.lightBackgroundColor,
    },

    /* ============ Date Filter Styles ============ */
    dateFilterContainer: {
        marginBottom: 12,
    },
    filterPeriodLabel: {
        marginBottom: 6,
        marginLeft: 4,
        letterSpacing: 0.5,
    },
    dateInputsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#E8F4FD',
        borderRadius: 14,
        padding: 6,
        borderWidth: 1,
        borderColor: '#D0E6FC',
    },
    dateBox: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 4,
        paddingHorizontal: 8,
    },
    dateDivider: {
        width: 1,
        height: '70%',
        backgroundColor: '#CBD5E1',
        marginHorizontal: 4,
    },

    /* ============ KPI Cards ============ */
    kpiRow: {
        flexDirection: 'row',
        gap: 14,
        marginBottom: 14,
    },
    statCard: {
        flex: 1,
        backgroundColor: appColors.light,
        padding: 16,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: '#F1F5F9',
        flexDirection: 'row',
        alignItems: 'center',
        elevation: 2,
        shadowColor: '#000',
        shadowOpacity: 0.03,
        shadowRadius: 6,
    },
    statCardPortrait: {
        width: '100%',
        padding: 14,
        borderRadius: 16,
        marginBottom: 10,
        backgroundColor: appColors.light,
        borderWidth: 1,
        borderColor: '#F1F5F9',
        elevation: 2,
        shadowColor: '#000',
        shadowOpacity: 0.03,
        shadowRadius: 6,
    },
    iconContainer: {
        width: 44,
        height: 44,
        borderRadius: 12,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 12,
    },
    statTextContainer: {
        flex: 1,
        justifyContent: 'center',
    },
    statTextContainerPortrait: {
        flex: 1,
        justifyContent: 'center',
    },

    /* ============ Staff Card Styles ============ */
    staffCard: {
        backgroundColor: appColors.light,
        borderRadius: 16,
        padding: 14,
        marginBottom: 10,
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1.5,
        borderColor: '#F1F5F9',
        elevation: 2,
        shadowColor: '#000',
        shadowOpacity: 0.03,
        shadowRadius: 6,
    },
    staffCardSelected: {
        borderColor: appColors.blue600,
        backgroundColor: '#F0F7FF',
    },
    avatar: {
        width: 42,
        height: 42,
        borderRadius: 21,
        backgroundColor: '#E0F2FE',
        justifyContent: 'center',
        alignItems: 'center',
    },
    staffInfo: {
        flex: 1,
        marginLeft: 12,
    },
    joinedContainer: {
        alignItems: 'flex-end',
        justifyContent: 'center',
    },

    /* ============ Landscape Layout ============ */
    landscapeLayout: {
        flex: 1,
        flexDirection: 'row',
        padding: 14,
        gap: 14,
    },
    landscapeSidebar: {
        flex: 0.32,
    },
    staffListContent: {
        paddingBottom: 20,
    },
    landscapeMain: {
        flex: 0.68,
    },
    tableCard: {
        flex: 1,
        backgroundColor: appColors.light,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: '#F1F5F9',
        elevation: 2,
        shadowColor: '#000',
        shadowOpacity: 0.03,
        shadowRadius: 8,
        overflow: 'hidden',
    },
    tableHeaderRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 14,
        paddingHorizontal: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
        backgroundColor: appColors.light,
    },
    tableRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 14,
        paddingHorizontal: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#F8FAFC',
    },
    colDate: {
        flex: 1.3,
    },
    colOrders: {
        flex: 1.1,
        textAlign: 'center',
    },
    colDiscount: {
        flex: 1.1,
        textAlign: 'center',
    },
    colNetSale: {
        flex: 1.1,
        textAlign: 'center',
    },
    colTotalSale: {
        flex: 1.1,
        textAlign: 'center',
    },
    colWorkingTime: {
        flex: 1.3,
        textAlign: 'right',
    },
    emptyContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 40,
    },

    /* ============ Mobile Layout ============ */
    mobileLayout: {
        flex: 1,
    },
    mobileContentContainer: {
        padding: 12,
        paddingBottom: 30,
    },
    mobileKpiContainer: {
        marginBottom: 8,
    },
    mobileSectionTitle: {
        marginBottom: 8,
        marginLeft: 4,
        letterSpacing: 0.5,
    },

    /* ============ Mobile Modal ============ */
    modalHeader: {
        width: '100%',
        padding: 16,
        backgroundColor: appColors.lightBackgroundColor,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
    },
    modalScrollContent: {
        padding: 16,
        paddingBottom: 40,
    },
    modalStaffCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: appColors.light,
        padding: 14,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: '#F1F5F9',
        elevation: 2,
    },
    avatarLarge: {
        width: 50,
        height: 50,
        borderRadius: 25,
        backgroundColor: '#E0F2FE',
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalDateCard: {
        backgroundColor: appColors.light,
        borderRadius: 14,
        padding: 14,
        marginBottom: 12,
        borderWidth: 1,
        borderColor: '#F1F5F9',
        elevation: 2,
    },
    modalDateCardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBottom: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
        marginBottom: 10,
    },
    dateTag: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#E8F4FD',
        paddingVertical: 4,
        paddingHorizontal: 10,
        borderRadius: 8,
    },
    timeTag: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F5F3FF',
        paddingVertical: 4,
        paddingHorizontal: 10,
        borderRadius: 8,
    },
    modalMetricsGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        gap: 8,
    },
    modalMetricItem: {
        width: '48%',
        backgroundColor: '#F8FAFC',
        padding: 10,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: '#F1F5F9',
    },
});

export default StaffReportPage;
